import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { CustomerList } from '../components/CustomerList'
import { CustomerForm } from '../components/CustomerForm'
import { CustomerProfile } from '../components/CustomerProfile'
import { PartnerList } from '../components/PartnerList'
import { PartnerForm, Partner } from '../components/PartnerForm'
import { Customer, getCustomerDisplayName, makeApiCall } from '../utils/customerUtils'
import { PageContent } from '../components/Layout'

type ViewMode = 'list' | 'add' | 'edit' | 'profile'
type TabMode = 'customers' | 'partners'

const Contacts: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [tabMode, setTabMode] = useState<TabMode>('customers')
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const [editReturnTo, setEditReturnTo] = useState<string | null>(null)

  // Handle URL parameters to auto-open specific customer from search or edit mode
  useEffect(() => {
    const customerId = searchParams.get('customerId')
    const editId = searchParams.get('edit')
    const returnTo = searchParams.get('returnTo')
    const tab = searchParams.get('tab') as TabMode
    
    if (tab && (tab === 'customers' || tab === 'partners')) {
      setTabMode(tab)
    }

    if (returnTo) {
      setEditReturnTo(returnTo)
    }
    
    if (customerId && !selectedCustomer && tabMode === 'customers') {
      fetchCustomerById(parseInt(customerId))
    } else if (editId && !selectedCustomer && tabMode === 'customers') {
      fetchCustomerByIdForEdit(parseInt(editId))
    }
  }, [searchParams, selectedCustomer, tabMode])

  const fetchCustomerById = async (customerId: number) => {
    try {
      const response = await makeApiCall(`/api/customers/${customerId}`)
      if (response.ok) {
        const data = await response.json()
        setSelectedCustomer(data.customer)
        setViewMode('profile')
        setSearchParams({})
      }
    } catch (error) {
      console.error('Error fetching customer:', error)
    }
  }

  const fetchCustomerByIdForEdit = async (customerId: number) => {
    try {
      const response = await makeApiCall(`/api/customers/${customerId}`)
      if (response.ok) {
        const data = await response.json()
        setSelectedCustomer(data.customer)
        setViewMode('edit')
        setSearchParams({})
      }
    } catch (error) {
      console.error('Error fetching customer:', error)
    }
  }

  const fetchPartnerById = async (partnerId: number) => {
    try {
      const response = await makeApiCall(`/api/partners/${partnerId}`)
      if (response.ok) {
        const data = await response.json()
        setSelectedPartner(data.partner)
        setViewMode('edit')
        setSearchParams({})
      }
    } catch (error) {
      console.error('Error fetching partner:', error)
    }
  }

  const handleAddCustomer = () => {
    setSelectedCustomer(null)
    setSelectedPartner(null)
    setTabMode('customers')
    setViewMode('add')
  }

  const handleAddPartner = () => {
    setSelectedCustomer(null)
    setSelectedPartner(null)
    setTabMode('partners')
    setViewMode('add')
  }

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer)
    setSelectedPartner(null)
    setViewMode('edit')
  }

  const handleEditPartner = (partner: Partner) => {
    setSelectedPartner(partner)
    setSelectedCustomer(null)
    setViewMode('edit')
  }

  const handleSelectCustomer = (customer: Customer) => {
    navigate(`/dashboard/customers/${customer.id}`)
  }

  const handleSelectPartner = (partner: Partner) => {
    // For now, just edit the partner. Later we could create a partner profile view
    handleEditPartner(partner)
  }

  const handleCustomerSubmit = (_customer: Customer) => {
    setRefreshTrigger(prev => prev + 1)
    if (editReturnTo) {
      navigate(editReturnTo)
      setEditReturnTo(null)
    } else {
      setViewMode('list')
      setSelectedCustomer(null)
    }
  }

  const handlePartnerSubmit = (_partner: Partner) => {
    setRefreshTrigger(prev => prev + 1)
    setViewMode('list')
    setSelectedPartner(null)
  }

  const handleCustomerDelete = () => {
    setRefreshTrigger(prev => prev + 1)
  }

  const handlePartnerDelete = () => {
    setRefreshTrigger(prev => prev + 1)
  }

  const handleCancel = () => {
    setViewMode('list')
    setSelectedCustomer(null)
    setSelectedPartner(null)
  }

  const handleCustomerClick = (customerId: number) => {
    navigate(`/dashboard/customers/${customerId}`)
  }

  const handleDealClick = (dealId: number) => {
    console.log('Navigate to deal:', dealId)
  }

  const handleTabChange = (tab: TabMode) => {
    setTabMode(tab)
    setViewMode('list')
    setSelectedCustomer(null)
    setSelectedPartner(null)
  }

  const renderHeader = () => {
    if (tabMode === 'customers') {
      switch (viewMode) {
        case 'add':
          return {
            title: t('customers.addCustomer'),
            subtitle: t('customers.addCustomerSubtitle')
          }
        case 'edit':
          return {
            title: `${t('customers.editCustomer')}: ${selectedCustomer ? getCustomerDisplayName(selectedCustomer) : t('common.unknown')}`,
            subtitle: t('customers.editCustomerSubtitle')
          }
        case 'profile':
          return {
            title: `${t('customers.customerProfile')}: ${selectedCustomer ? getCustomerDisplayName(selectedCustomer) : t('common.unknown')}`,
            subtitle: t('customers.customerProfileSubtitle')
          }
        default:
          return {
            title: t('customers.title'),
            subtitle: t('customers.subtitle')
          }
      }
    } else {
      // Partners tab
      switch (viewMode) {
        case 'add':
          return {
            title: t('partners.addPartner'),
            subtitle: t('partners.subtitle')
          }
        case 'edit':
          return {
            title: `${t('partners.editPartner')}: ${selectedPartner ? selectedPartner.name : t('common.unknown')}`,
            subtitle: t('partners.subtitle')
          }
        default:
          return {
            title: t('customers.title'),
            subtitle: t('customers.subtitle')
          }
      }
    }
  }

  const header = renderHeader()

  return (
    <PageContent>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{header.title}</h1>
            {(viewMode === 'edit' || viewMode === 'profile') && <p className="text-gray-600 dark:text-gray-300 mt-2">{header.subtitle}</p>}
          </div>
          <div className="flex items-center space-x-3">
            {viewMode !== 'list' && (
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                ← {tabMode === 'customers' ? t('customers.backToList') : t('partners.backToList')}
              </button>
            )}
            {viewMode === 'list' && (
              <button
                onClick={tabMode === 'customers' ? handleAddCustomer : handleAddPartner}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {tabMode === 'customers' ? t('customers.addCustomer') : t('partners.addPartner')}
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        {viewMode === 'list' && (
          <div className="mt-6 border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => handleTabChange('customers')}
                className={`${
                  tabMode === 'customers'
                    ? 'border-gray-900 text-gray-900 dark:border-gray-100 dark:text-gray-100'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                {t('customers.customersTab')}
              </button>
              <button
                onClick={() => handleTabChange('partners')}
                className={`${
                  tabMode === 'partners'
                    ? 'border-gray-900 text-gray-900 dark:border-gray-100 dark:text-gray-100'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                {t('customers.partnersTab')}
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Content */}
      {viewMode === 'list' && tabMode === 'customers' && (
        <CustomerList
          key={refreshTrigger}
          onSelectCustomer={handleSelectCustomer}
          onEditCustomer={handleEditCustomer}
          onDeleteCustomer={handleCustomerDelete}
          showActions={true}
        />
      )}

      {viewMode === 'list' && tabMode === 'partners' && (
        <PartnerList
          key={refreshTrigger}
          onSelectPartner={handleSelectPartner}
          onEditPartner={handleEditPartner}
          onDeletePartner={handlePartnerDelete}
          showActions={true}
        />
      )}

      {(viewMode === 'add' || viewMode === 'edit') && tabMode === 'customers' && (
        <CustomerForm
          customer={selectedCustomer || undefined}
          onSubmit={handleCustomerSubmit}
          onCancel={handleCancel}
          isModal={false}
        />
      )}

      {(viewMode === 'add' || viewMode === 'edit') && tabMode === 'partners' && (
        <PartnerForm
          partner={selectedPartner || undefined}
          onSubmit={handlePartnerSubmit}
          onCancel={handleCancel}
          isModal={false}
        />
      )}

      {viewMode === 'profile' && selectedCustomer && tabMode === 'customers' && (
        <CustomerProfile
          customerId={selectedCustomer.id}
          onEdit={handleEditCustomer}
          onClose={handleCancel}
          onCustomerClick={handleCustomerClick}
          onDealClick={handleDealClick}
        />
      )}
    </PageContent>
  )
}

export default Contacts

